import {Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Countries, EmailRegex, PhoneRegex} from "../../../constants/general-variables";
import {AuthenticationHttpService} from "../../../services/http-services/authentication/authentication-http.service";
import {AuthenticationHelperService} from "../../../services/helpers/authentication-helper.service";
import {PAGES_AND_PATHS} from "../../../constants/pages-and-paths";
import {ToastrService} from "ngx-toastr";
import {UiHelperService} from "../../../services/helpers/ui-helper.service";
import {Meta, Title} from '@angular/platform-browser';
import {ProfileHttpService} from "../../../services/http-services/profile/profile-http.service";
import {debounceTime} from "rxjs/operators";
import {Subject} from "rxjs";

@Component({
  selector: 'tl-create-company-account',
  templateUrl: './create-company-account.component.html',
})
export class CreateCompanyAccountComponent implements OnInit, OnDestroy  {
  isPasswordVisible = false;
  isConfirmPasswordVisible = false;
  formTriedToSubmit = false;
  createCompanyAccountForm!: FormGroup;
  isSpinnerActive = false;
  companyMailSuffix = '@';
  isAreaCodeOpen =  false;
  taken = false;
  available = [];
  selectedArea = Countries[0];
  @ViewChild('areaCodeBtn') areaCodeBtn: ElementRef | undefined;
  protected readonly PAGES_AND_PATHS = PAGES_AND_PATHS;
  protected readonly Countries = Countries;

  private readonly debounceTimeMs = 500;
  private searchSubject = new Subject<string>();
  constructor(private formBuilder: FormBuilder,
              private toastService: ToastrService,
              private renderer: Renderer2,
              private uiHelperService: UiHelperService,
              private authHelperService: AuthenticationHelperService,
              private authHttpService: AuthenticationHttpService,
              private title: Title,
              private meta: Meta,
              private profileHttpService: ProfileHttpService) { }

  ngOnInit(): void {
    this.title.setTitle('TheLysts, List it, Live it! - Create Company Account');
    this.meta.updateTag({name: 'description', content: 'Everybody has something to recommend. And if you are an expert in a subject, your recommendations are really valuable. And may generate good revenue. Use your expertise, either as an enthusiast or a professional, and create lists with valuable tips. Create your company account Today!'});
    this.meta.updateTag({name: 'keywords', content: 'thelysts,lyst,list,task,organizer,company,trip,suggestion,music,food,restaurants,sports,movies,films,tv series,podcasts,life style,cars,boats,app,recommendation,planner,beauty,fashion,celebrities,influencers,museums,golf,ongs'});
    this.meta.updateTag({property: 'og:title', content: 'TheLysts, List it, Live it! - Create Company Account'});
    this.meta.updateTag({property: 'og:image', content: 'https://thelysts.com/og-image/og-image-create-company-account.jpg'});
    this.meta.updateTag({property: 'og:description', content: 'Everybody has something to recommend. And if you are an expert in a subject, your recommendations are really valuable. And may generate good revenue. Use your expertise, either as an enthusiast or a professional, and create lists with valuable tips. Create your company account Today!'});

    this.buildForm();

    this.createCompanyAccountForm.controls.company_website.valueChanges.subscribe(val => {
      const trimmedValue = val.trim();
      if (val !== trimmedValue) {
        this.createCompanyAccountForm.controls.company_website.setValue(trimmedValue, { emitEvent: false });
      }
      if (val.endsWith('/')) {
        this.createCompanyAccountForm.controls.company_website.setValue(val.slice(0, -1), { emitEvent: false });
      }
      this.companyMailSuffix = '@' + val.trim();
      if (this.companyMailSuffix.startsWith('@https://www.')) {
        this.companyMailSuffix = this.companyMailSuffix.replace('@https://www.' , '@');
      }
      if (this.companyMailSuffix.startsWith('@https://')) {
        this.companyMailSuffix = this.companyMailSuffix.replace('@https://' , '@');
      }
      if (this.companyMailSuffix.endsWith('/')) {
        this.companyMailSuffix = this.companyMailSuffix.slice(0, -1);
      }
      this.createCompanyAccountForm.controls.email.patchValue(val.trim() + this.companyMailSuffix);
    });
    this.createCompanyAccountForm.controls.emailForView.valueChanges.subscribe(val => {
      const trimmedValue = val.trim();
      if (val !== trimmedValue) {
        this.createCompanyAccountForm.controls.emailForView.setValue(trimmedValue, { emitEvent: false });
      }
      this.createCompanyAccountForm.controls.email.markAsTouched();
      this.createCompanyAccountForm.controls.email.patchValue(val.trim() + this.companyMailSuffix);
    });

    this.createCompanyAccountForm.controls.username.valueChanges.subscribe(value => {
      const valueWithoutSpaces = value.replaceAll(' ', '');
      if (value !== valueWithoutSpaces) {
        this.createCompanyAccountForm.controls.username.setValue(valueWithoutSpaces, { emitEvent: false });
      }
    });
    this.listenToClickAreaCode();

    this.searchSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((searchValue) => {
      this.onUsernameChange(searchValue);
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  onSearch(): void {
    if (this.createCompanyAccountForm.controls.username.value) {
      this.searchSubject.next(this.createCompanyAccountForm.controls.username.value);
    }
  }

  listenToClickAreaCode(): void {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (this.uiHelperService.checkIfClickedOutsideOfTheElement(this.areaCodeBtn, this.areaCodeBtn, e)) {
        this.isAreaCodeOpen = false;
      }
    });
  }

  buildForm(): void {
    this.createCompanyAccountForm = this.formBuilder.group({
      full_name: ['', [Validators.required]],
      company_name: ['', [Validators.required]],
      company_website: ['', [Validators.required]],
      emailForView: [''],
      email: ['', [Validators.required, Validators.pattern(EmailRegex)]],
      phone_number: ['', [Validators.required, Validators.pattern(PhoneRegex)]],
      username: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      tos: [false, Validators.requiredTrue]
    });

    this.createCompanyAccountForm.addValidators(
      this.matchFields('password', 'confirmPassword')
    );
  }

  onSubmitCreateCompanyAccountForm(): void {
    this.formTriedToSubmit = true;
    if (this.createCompanyAccountForm.valid) {
      this.isSpinnerActive = true;
      this.authHttpService.postCompanyRegister({...this.createCompanyAccountForm.value, phone_number: this.selectedArea.phone_code + ' ' + this.createCompanyAccountForm.controls.phone_number.value}).then(res => {
        this.isSpinnerActive = false;
        this.authHelperService.clientLogin(res.data.user, res.data.token, PAGES_AND_PATHS.verifyAccountCompany.pagePath);
      }).catch(({error}: {error: { errors: { [key: string]: string }}}) => {
        this.isSpinnerActive = false;
        Object.values(error.errors).forEach(error => this.toastService.error());
      });
    }
  }

  onSelectCodeArea(country: any): void {
    this.selectedArea = country;
    this.isAreaCodeOpen = false;
  }

  onUsernameChange(value: string): void {
    this.profileHttpService
      .checkUsername(value)
      .then((r: any) => {
        this.taken = r.exists;
        this.available = r.available;
      });
  }

  matchFields(fieldToMatch: string, fieldToValidate: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const controlToMatch = formGroup.get(fieldToMatch);
      const controlToValidate = formGroup.get(fieldToValidate);

      if (!controlToMatch || !controlToValidate) {
        return null;
      }

      if (controlToMatch.value !== controlToValidate.value) {
        controlToValidate.setErrors({ mustMatch: true });
        return { mustMatch: true };
      } else {
        if (controlToValidate.hasError('mustMatch')) {
          controlToValidate.setErrors(null);
        }
        return null;
      }
    };
  }
}
